import { useInfiniteQuery } from "@tanstack/react-query";
import {
  userService,
  UserFilters,
  UsersResponse,
} from "../api/services/userService";
import type { User } from "../schemas/userSchema";
import { useUsers } from "./useUsers";
import { DEFAULT_PAGE_SIZE } from "../constants";

export type UsersInfiniteFilters = Omit<UserFilters, "page" | "pageSize"> & {
  pageSize?: number;
};

export const useUsersInfinite = (filters: UsersInfiniteFilters = {}) => {
  const { pageSize = DEFAULT_PAGE_SIZE, ...rest } = filters;
  const mutations = useUsers(undefined, { enabled: false });

  const query = useInfiniteQuery<UsersResponse, Error>({
    queryKey: ["users", "infinite", { ...rest, pageSize }],
    queryFn: ({ pageParam }) =>
      userService.getAll({
        ...rest,
        page: pageParam as number,
        pageSize,
      }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) =>
      lastPage.hasNextPage ? (lastPage.page ?? 0) + 1 : undefined,
  });

  const users: User[] = query.data?.pages.flatMap((p) => p.users) ?? [];
  const total = query.data?.pages[0]?.total ?? 0;

  return {
    ...mutations,
    users,
    total,
    isLoading: query.isLoading || mutations.isLoading,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: !!query.hasNextPage,
    fetchNextPage: query.fetchNextPage,
    refetch: query.refetch,
  };
};
